export interface PushSubscriptionInfo {
  id: number
  browserName: string | null
  platform: string | null
  permissionState: string | null
  lastSeenAt: string | null
  lastSuccessfulPushAt: string | null
}

export interface PushSubscriptionStatus {
  vapidPublicKey: string | null
  vapidConfigured: boolean
  activeSubscriptionCount: number
  subscriptions: PushSubscriptionInfo[]
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ||
  ""

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

async function request<Payload>(
  url: string,
  options: RequestInit = {},
): Promise<Payload> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...jsonHeaders,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as
      | { error?: string }
      | null
    throw new Error(error?.error ?? "Could not update push notifications")
  }

  return response.json() as Promise<Payload>
}

export const pushSubscriptionsApi = {
  status() {
    return request<PushSubscriptionStatus>("/push_subscription")
  },

  create(subscription: globalThis.PushSubscription) {
    return request<PushSubscriptionStatus>("/push_subscription", {
      method: "POST",
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        permission_state: Notification.permission,
        browser_name: browserName(),
        platform: navigator.platform,
      }),
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },

  destroy(subscription?: globalThis.PushSubscription | null) {
    return request<PushSubscriptionStatus>("/push_subscription", {
      method: "DELETE",
      body: JSON.stringify({
        endpoint: subscription?.endpoint,
        permission_state: Notification.permission,
      }),
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },

  test() {
    return request<{ ok: true }>("/push_subscription/test", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },

  dismissPrompt() {
    return request<PushSubscriptionStatus>("/push_subscription/dismiss_prompt", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  )
}

export async function currentBrowserSubscription() {
  const registration = await serviceWorkerRegistration()

  return registration.pushManager.getSubscription()
}

export async function subscribeCurrentBrowser(vapidPublicKey: string) {
  const registration = await serviceWorkerRegistration()

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  })
}

async function serviceWorkerRegistration() {
  const existingRegistration = await navigator.serviceWorker.getRegistration("/")

  return (
    existingRegistration ??
    navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
  )
}

export async function unsubscribeCurrentBrowser() {
  const subscription = await currentBrowserSubscription()
  await subscription?.unsubscribe()

  return subscription
}

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index)
  }

  return outputArray
}

function browserName() {
  const agent = navigator.userAgent

  if (agent.includes("Edg/")) return "Edge"
  if (agent.includes("Chrome/")) return "Chrome"
  if (agent.includes("Firefox/")) return "Firefox"
  if (agent.includes("Safari/")) return "Safari"

  return "Unknown browser"
}
